import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { Geolocation } from '@capacitor/geolocation';
import { CodaltComponent } from 'app/codalt.component';
import { LocalStorage } from 'app/local-storage.class';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Region } from '../region.class';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { Settings } from '../settings.class';
import { Utils } from '../utils.class';

@Component({
    template: '',
    standalone: false,
})
export class GoogleMapsComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('googleMap') googleMap: GoogleMap;
    mapTypeId = 'roadmap' as google.maps.MapTypeId;
    subscriptions = new Subscription();
    private readonly centerOrZoomSettled = new Subject<void>();
    private headingInterval;
    private gpsWatchId: string;

    options: google.maps.MapOptions;

    // UI state (segment details are now always visible)
    gpsLocation: { lat: number; lng: number };
    viewState = {
        gpsFailure: false,
    };
    followLocation = false;

    toggleMapType() {
        const usedTypes = [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.HYBRID];
        this.mapTypeId = usedTypes[usedTypes.indexOf(this.mapTypeId) + 1] ?? (usedTypes[0] as any);
    }

    constructor(
        protected confirmDialogService: ConfirmDialogService,
        protected cdref: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {
        this.setupGeo();

        this.options = {
            mapId: '721b75f40e9d44452387af4b',
            center: { lat: 52.5, lng: 5 },
            zoom: 9,
            tilt: 0,
            heading: 0,
            isFractionalZoomEnabled: false,
            headingInteractionEnabled: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            mapTypeControl: false,
            zoomControl: false,
            streetViewControl: true,
            streetViewControlOptions: {
                position:
                    window.innerWidth < 768
                        ? google.maps.ControlPosition.LEFT_CENTER
                        : google.maps.ControlPosition.RIGHT_BOTTOM,
            },
            restriction: Utils.RestrictToNl(),
        };
    }

    ngAfterViewInit() {
        Settings.mapCenter = { lng: this.googleMap.getCenter().lng(), lat: this.googleMap.getCenter().lat() };

        const changed = () => {
            this.options.center = { lng: this.googleMap.getCenter().lng(), lat: this.googleMap.getCenter().lat() };
            this.options.zoom = this.googleMap.getZoom();
            const previousZoom = Settings.mapZoom;
            Settings.mapZoom = this.googleMap.getZoom();
            const previousCenter = JSON.stringify(Settings.mapCenter);
            Settings.mapCenter = this.options.center;
            const centerHasChanged = previousCenter !== JSON.stringify(Settings.mapCenter);
            const zoomHasChanged = previousZoom !== Settings.mapZoom;
            if (centerHasChanged) {
                this.centerChanged();
            }
            if (zoomHasChanged) {
                this.zoomChanged();
            }
            if (centerHasChanged || zoomHasChanged) {
                this.centerOrZoomSettled.next();
            }
        };

        this.subscriptions.add(this.googleMap.mapDragend.pipe(debounceTime(10)).subscribe(() => changed()));
        this.subscriptions.add(this.googleMap.centerChanged.pipe(debounceTime(10)).subscribe(() => changed()));
        this.subscriptions.add(this.googleMap.zoomChanged.pipe(debounceTime(10)).subscribe(() => changed()));
        this.subscriptions.add(
            this.centerOrZoomSettled.pipe(debounceTime(400)).subscribe(() => this.centerOrZoomChanged())
        );

        Settings.mapZoom = this.googleMap.getZoom();
    }

    centerChanged() {
        if (!this.gpsLocation) {
            return;
        }
        const latDiff = Math.abs(this.googleMap.getCenter().lat() - this.gpsLocation.lat);
        const longDiff = Math.abs(this.googleMap.getCenter().lng() - this.gpsLocation.lng);
        if (this.followLocation && (latDiff > 0.00001 || longDiff > 0.00001)) {
            this.followLocation = false;
        }
    }

    zoomChanged() {}

    centerOrZoomChanged() {}

    centerCountry() {
        const countryPoints = [
            { lat: 53.49785, lng: 4.682922 },
            { lat: 53.396432, lng: 7.393799 },
            { lat: 50.75731, lng: 6.011581 },
            { lat: 51.238706, lng: 3.269119 },
        ];
        const region = new Region(countryPoints);
        this.zoomCenter(region.bestMapView(window.innerWidth, window.innerHeight, 20), region.centroid());
        this.resetHeading();
    }

    public zoomCenter(zoom: number, center: { lat: number; lng: number }) {
        this.options.zoom = zoom;
        this.applyOptions();
        // Needed because RestrictToNl could prevent moving when zoom has not been handled
        setTimeout(() => this.setMapCenter(center));
    }

    private applyOptions() {
        this.options = Object.assign({}, this.options);
    }

    setMapCenter(center: { lat: number; lng: number }) {
        // Without this an empty centroid would move the map to 0,0 instead of leaving it alone.
        if (!center) {
            return;
        }
        if (this.googleMap) {
            this.options.center = { lat: +center.lat, lng: +center.lng };
            this.applyOptions();
        }
    }

    async setupGeo(center = false) {
        this.followLocation = center;
        await Geolocation.requestPermissions().catch(() => {});
        if (this.gpsWatchId) {
            await Geolocation.clearWatch({ id: this.gpsWatchId });
        }

        const setLocation = (location, err?: any) => {
            if (err || !location) {
                this.gpsLocation = null;
                this.viewState.gpsFailure = true;
                this.cdref.detectChanges();
                return;
            }
            this.viewState.gpsFailure = false;
            LocalStorage.setLatestLocation(location.coords);
            this.gpsLocation = {
                lat: location.coords.latitude,
                lng: location.coords.longitude,
            };
            if (this.followLocation) {
                this.followGpsLocation();
            }
            this.cdref.detectChanges();
        };

        Geolocation.getCurrentPosition({ enableHighAccuracy: true }).then(location => setLocation(location));
        this.gpsWatchId = await Geolocation.watchPosition({ enableHighAccuracy: true }, (location, err) => {
            setLocation(location, err);
        });
    }

    centerGps() {
        console.log(this.gpsLocation, this.viewState.gpsFailure);
        if (this.gpsLocation && !this.viewState.gpsFailure) {
            this.followLocation = true;
            const currentCenter = this.googleMap.getCenter();
            if (currentCenter.lng() === this.gpsLocation.lng && currentCenter.lat() === this.gpsLocation.lat) {
                this.resetHeading();
            }
            this.followGpsLocation();
        } else {
            this.confirmDialogService
                .confirm(
                    'Locatie niet toegestaan',
                    'Geef toegang tot je locatie om deze functionaliteit te gebruiken.',
                    'Opnieuw proberen',
                    'Geen GPS gebruiken'
                )
                .then(() => this.setupGeo(true))
                .catch(() => {});
        }
    }

    // Follow GPS at zoom ≥17; skip jitter <5m and avoid rebuilding map options.
    private followGpsLocation() {
        if (this.googleMap.getZoom() < 17) {
            this.zoomCenter(17, this.gpsLocation);
            return;
        }
        const center = this.googleMap.getCenter();
        if (center && google.maps.geometry.spherical.computeDistanceBetween(center, this.gpsLocation) < 5) {
            return;
        }
        this.googleMap.googleMap?.setCenter(this.gpsLocation);
    }

    public headingStart() {
        this.headingStop();
        this.headingInterval = setInterval(() => {
            this.options.heading -= 10;
            this.applyOptions();
        }, 100);
    }

    public headingSingle() {
        this.options.heading -= 10;
        this.applyOptions();
    }

    public headingStop() {
        if (this.headingInterval) {
            clearInterval(this.headingInterval);
        }
    }

    resetHeading() {
        this.options.heading = 0;
        this.applyOptions();
    }

    ngOnDestroy(): void {
        this.headingStop();
        this.subscriptions.unsubscribe();
        if (this.gpsWatchId) {
            Geolocation.clearWatch({ id: this.gpsWatchId });
        }
    }
}
